import { useState } from 'react';
import { apiFetch } from '../config/api';
import { getAdminPortalAccessError } from '../lib/adminAccess';
import { sendSupportRequest } from '../lib/support';
import SchoolSupportModal from '../components/SchoolSupportModal';

export default function Login({ onLogin, onReset }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [supportView, setSupportView] = useState(null);
  const [supportSuccessType, setSupportSuccessType] = useState(null);
  const [supportSending, setSupportSending] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [supportDrafts, setSupportDrafts] = useState({
    report: '',
    question: '',
    feature: '',
  });

  const closeSupport = () => {
    setSupportView(null);
    setSupportSuccessType(null);
    setSupportError(null);
  };

  const handleSupportDraftChange = (view, value) => {
    setSupportDrafts((current) => ({ ...current, [view]: value }));
    if (supportError) setSupportError(null);
  };

  const handleSupportSend = async (view) => {
    const message = supportDrafts[view]?.trim();
    if (!message || supportSending) return;

    setSupportError(null);
    setSupportSending(true);

    try {
      await sendSupportRequest({
        supportType: view,
        message,
        userEmail: email.trim() || null,
      });
      setSupportDrafts((current) => ({ ...current, [view]: '' }));
      setSupportSuccessType(view);
      setSupportView('success');
    } catch (err) {
      console.error('Failed to send support message:', err);
      setSupportError("We couldn't send your message. Please try again.");
    } finally {
      setSupportSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const accessError = getAdminPortalAccessError(data.user);
      if (accessError) {
        throw new Error(accessError);
      }
      onLogin({ ...data.user, token: data.token });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sa-login-page">
      <div className="sa-login-card">
        <h1 className="sa-login-brand">car-pool</h1>
        <h2 className="sa-login-title">Manager Admin Portal</h2>
        <p className="sa-login-subtitle">
          Secure dashboard access for school administrators
          <br />and carpool managers
        </p>

        {error && <div className="sa-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="username / email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit" className="sa-login-btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Login to Admin Portal'}
          </button>
        </form>

        <div className="sa-login-links">
          <p>
            Forgot your password?{' '}
            <button onClick={onReset}>Click here to reset it</button>
          </p>
          <p>
            Need help accessing your account?
            <br />
            <button type="button" onClick={() => setSupportView('menu')}>Contact support.</button>
          </p>
        </div>
      </div>

      <SchoolSupportModal
        view={supportView}
        successType={supportSuccessType}
        drafts={supportDrafts}
        sending={supportSending}
        error={supportError}
        successButtonLabel="Done"
        onClose={closeSupport}
        onBack={() => {
          setSupportSuccessType(null);
          setSupportError(null);
          setSupportView('menu');
        }}
        onSelectView={(view) => {
          setSupportSuccessType(null);
          setSupportError(null);
          setSupportView(view);
        }}
        onDraftChange={handleSupportDraftChange}
        onSend={handleSupportSend}
        onReturnToDashboard={closeSupport}
      />
    </div>
  );
}
